import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { getCountryName } from '@/utils/country';
import { useFetch } from '@/utils/fetch';

export type Address = {
  id: string;
  contactName?: string | null;
  contactEmail?: string | null;
  line1: string | null;
  line2?: string | null;
  zipCode: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
};

export const formatAddress = (address: Address) =>
  [
    address.line1,
    address.line2,
    address.zipCode,
    address.city,
    address.state,
    getCountryName(address.country),
  ]
    .filter((el) => !!el)
    .join(' ');

export const formatStreet = (address: Address) =>
  [address.line1, address.line2].filter((el) => !!el).join(' ');

export const formatZip = (address: Address) =>
  [address.zipCode, address.city, address.state, getCountryName(address.country)]
    .filter((el) => !!el)
    .join(' ');

export const formatCityState = (address: Address) =>
  [address.city, address.state].filter((el) => !!el).join(', ');

export const isAddressEmpty = (address: Partial<AddressRaw> | null | undefined) =>
  !address?.line1 && !address?.zipCode && !address?.city && !address?.country;

export interface AddressRaw {
  contactName?: string | null;
  contactEmail?: string | null;
  line1: string | null;
  line2?: string | null;
  zipCode: string | null;
  city: string | null;
  state?: string | null;
  country: string | null;
}

export const useAddresses = (
  params: Endpoints['GET /addresses']['query'],
  options?: {
    keepPreviousData?: boolean;
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /addresses']>();

  return useQuery({
    queryKey: ['addresses', params],
    queryFn: () => fetch('/addresses', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled,
  });
};
